import { AccordionDisclosure } from 'theme';

// Smile SDK contract: https://dev.smile.io/js/resources/customer-vip-status/object
const TIER_KEYS = ['bronze', 'silver', 'gold', 'platinum'];
const SMILE_TIMEOUT = 10000;
const SMILE_PANEL_VIEWS = {
  activity: {
    deepLink: 'home',
    selector: 'img[src*="/images/history-icon.svg"]'
  },
  rewards: {
    deepLink: 'home',
    selector: '[data-testid="view-your-rewards"]'
  },
  birthday: {
    deepLink: 'points_activity_rules',
    selector: '[data-rule-type="customer_birthday"] [data-testid="smile-ui-activity-rule-action"]'
  }
};

export function normalizeMembership(wallet, vipStatus) {
  if (!wallet || !Number.isFinite(wallet.balance) || !vipStatus || !('vipTier' in vipStatus)) {
    throw new Error('Incomplete Smile membership data');
  }
  const tierName = vipStatus.vipTier?.name || null;
  const tierKey = tierName?.trim().toLowerCase();
  const nextTier = vipStatus.nextVipTier?.name || null;
  const highest = Boolean(tierName && vipStatus.nextVipTier === null && vipStatus.deltaToNextVipTier === null);
  let progress = null;
  if (highest) {
    progress = 100;
  } else if (nextTier && Number.isFinite(vipStatus.progressValue) && Number.isFinite(vipStatus.deltaToNextVipTier)) {
    const completed = Math.max(0, vipStatus.progressValue);
    const remaining = Math.max(0, vipStatus.deltaToNextVipTier);
    progress = completed + remaining > 0 ? Math.round(completed / (completed + remaining) * 100) : 0;
  }
  return {
    status: 'ready',
    points: wallet.balance,
    tierName,
    tierKey: TIER_KEYS.includes(tierKey) ? tierKey : null,
    nextTier,
    progress,
    highest
  };
}

function abortError() {
  return new DOMException('Membership view disconnected', 'AbortError');
}

export function waitForSmileCustomer(signal, timeout = SMILE_TIMEOUT) {
  return new Promise((resolve, reject) => {
    let poll;
    let deadline;
    let loaderRequested = false;
    const cleanup = () => {
      clearInterval(poll);
      clearTimeout(deadline);
      document.removeEventListener('smile-js-initialized', check);
      signal?.removeEventListener('abort', cancel);
    };
    const finish = (error, sdk) => {
      cleanup();
      if (error) reject(error);
      else resolve(sdk);
    };
    const cancel = () => finish(abortError());
    const check = () => {
      try {
        const sdk = window.Smile;
        if (sdk?.customer?.current()) {
          finish(null, sdk);
          return;
        }
      } catch {
        // The SDK can be present before Shopify's customer initialization finishes.
      }
      // Smile's Shopify lite launcher defers the SDK. Use its existing loader,
      // which retains app-managed credentials and does not open the rewards panel.
      if (!loaderRequested && typeof window.loadSmileSdk === 'function') {
        loaderRequested = true;
        window.loadSmileSdk();
      }
    };
    if (signal?.aborted) return cancel();
    document.addEventListener('smile-js-initialized', check);
    signal?.addEventListener('abort', cancel, { once: true });
    poll = setInterval(check, 250);
    deadline = setTimeout(() => finish(new Error('Smile customer initialization timed out')), timeout);
    check();
  });
}

export function boundedRequest(promise, signal) {
  return new Promise((resolve, reject) => {
    const cancel = () => finish(abortError());
    const timer = setTimeout(() => finish(new Error('Smile request timed out')), SMILE_TIMEOUT);
    const finish = (error, value) => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', cancel);
      if (error) reject(error);
      else resolve(value);
    };
    if (signal?.aborted) return cancel();
    signal?.addEventListener('abort', cancel, { once: true });
    promise.then(value => finish(null, value), error => finish(error));
  });
}

function isSmilePanelVisible() {
  const frame = document.querySelector('.smile-panel-frame');
  return Boolean(frame && frame.getClientRects().length && getComputedStyle(frame).visibility !== 'hidden');
}

function waitWhile(shouldWait, error, signal) {
  return new Promise((resolve, reject) => {
    const startedAt = performance.now();
    let timer;
    const cleanup = () => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', cancel);
    };
    const finish = (reason, value) => {
      cleanup();
      if (reason) reject(reason);
      else resolve(value);
    };
    const cancel = () => finish(abortError());
    const check = () => {
      try {
        const value = shouldWait();
        if (!value) {
          finish(null);
          return;
        }
        if (value !== true) {
          finish(null, value);
          return;
        }
      } catch {
        // Smile's iframe can throw while it is swapping documents.
      }
      if (performance.now() - startedAt >= SMILE_TIMEOUT) finish(new Error(error));
      else timer = setTimeout(check, 50);
    };
    if (signal?.aborted) return cancel();
    signal?.addEventListener('abort', cancel, { once: true });
    check();
  });
}

function waitForSmilePanelHidden(signal) {
  return waitWhile(isSmilePanelVisible, 'Smile panel did not close', signal);
}

function waitForSmilePanelView(view, signal) {
  return waitWhile(() => {
    try {
      const panel = document.querySelector('.smile-panel-frame')?.contentDocument;
      const marker = panel?.querySelector(SMILE_PANEL_VIEWS[view].selector);
      // Smile may put its click handler on a nested role=button chevron.
      // Choose the nearest interactive control, including the marker itself.
      return marker?.closest('button, [role="button"]') || true;
    } catch {
      return true;
    }
  }, `Smile ${view} view is unavailable`, signal);
}

let panelSession;

function beginPanelSession(signal) {
  panelSession?.abort();
  const session = new AbortController();
  panelSession = session;
  if (signal?.aborted) session.abort();
  else signal?.addEventListener('abort', () => session.abort(), { once: true });
  return session;
}

async function resetSmilePanel(signal) {
  if (!isSmilePanelVisible() || typeof window.SmileUI.closePanel !== 'function') return;
  window.SmileUI.closePanel();
  await waitForSmilePanelHidden(signal);
}

export async function openSmilePanel(link, signal) {
  const session = beginPanelSession(signal);
  const { signal: nav } = session;
  try {
    await boundedRequest(Promise.resolve().then(() => window.SmileUI.ready()), nav);
    const view = link.dataset.loyaltyView;
    const panelView = SMILE_PANEL_VIEWS[view];
    await resetSmilePanel(nav);
    if (!panelView) {
      await window.SmileUI.openPanel({ deep_link: link.dataset.loyaltyPanel });
      return;
    }

    // Smile does not expose deep links for these destination views. Open the
    // closest public view first, then use the controls owned by the panel.
    await window.SmileUI.openPanel({ deep_link: panelView.deepLink });
    const control = await waitForSmilePanelView(view, nav);
    if (nav.aborted) throw abortError();
    control.click();
  } finally {
    if (panelSession === session) panelSession = null;
  }
}

// Only known social destinations may be opened by earning cards; never Smile panel links.
export function socialActionUrl(value, action) {
  const domains = { facebook: 'facebook.com', instagram: 'instagram.com', tiktok: 'tiktok.com' };
  try {
    const url = new URL(value);
    const domain = domains[action];
    if (!domain || !['https:', 'http:'].includes(url.protocol) || url.username || url.password) return null;
    if (url.hostname !== domain && !url.hostname.endsWith(`.${domain}`)) return null;
    return url.href;
  } catch { return null; }
}

function webUrl(value) {
  try {
    const url = new URL(value);
    return ['http:', 'https:'].includes(url.protocol) && !url.username && !url.password ? url.href : null;
  } catch { return null; }
}

function unusedFulfillment(list) {
  if (!Array.isArray(list)) return null;
  const now = Date.now();
  return list.find(item => {
    const status = item.fulfillmentStatus || item.fulfillment_status;
    const used = item.usedAt ?? item.used_at;
    const expires = item.expiresAt ?? item.expires_at;
    if (status !== 'issued' || used) return false;
    if (expires && Date.parse(expires) <= now) return false;
    return true;
  }) || null;
}

function pointsRecord(item) {
  return item?.pointsProduct || item || {};
}

function variableField(item, camel, snake) {
  const value = Number(pointsRecord(item)[camel] ?? pointsRecord(item)[snake]);
  return Number.isFinite(value) ? value : null;
}

function isVariableProduct(item) {
  const type = pointsRecord(item).exchangeType || pointsRecord(item).exchange_type;
  return type === 'variable';
}

function productName(item) {
  return String(item?.name || pointsRecord(item).name || item?.reward?.name || pointsRecord(item).reward?.name || '');
}

function fitsVariableSpend(item, points) {
  const min = variableField(item, 'variablePointsMin', 'variable_points_min');
  const max = variableField(item, 'variablePointsMax', 'variable_points_max');
  const step = variableField(item, 'variablePointsStep', 'variable_points_step');
  if (min != null && points < min) return false;
  if (max != null && points > max) return false;
  if (step != null && step > 0 && points % step !== 0) return false;
  return true;
}

export function orderDiscountProduct(products, strict = false) {
  const list = products.filter(isVariableProduct);
  return list.find(item => /order\s*discount/i.test(productName(item))) || (strict ? null : list[0]) || null;
}

function fillCount(template, count) {
  const formatted = new Intl.NumberFormat(document.documentElement.lang || 'en').format(count);
  const frag = document.createDocumentFragment();
  String(template || 'Need {count} more Points').split(/\{\{\s*count\s*\}\}|\{count\}/).forEach((part, index) => {
    if (index) {
      const strong = document.createElement('strong');
      strong.textContent = formatted;
      frag.append(strong);
    }
    if (part) frag.append(document.createTextNode(part));
  });
  return frag;
}

function emptyState() {
  return { customer: null, customerStatus: 'loading', wallet: { status: 'loading' }, vip: { status: 'loading' }, rules: { status: 'loading' }, products: { status: 'loading' } };
}

class LoyaltyProgram extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.addEventListener('click', event => this.handleClick(event), { signal: this.controller.signal });
    document.addEventListener('smile-js-initialized', () => {
      if (this.state?.customerStatus === 'error') this.load();
    }, { signal: this.controller.signal });
    this.load();
  }

  disconnectedCallback() {
    this.controller?.abort();
    panelSession?.abort();
    this.social?.disconnect();
    this.social = null;
    this.requestController?.abort();
  }

  publish() {
    this.render();
    this.dispatchEvent(new CustomEvent('loyalty:data', { detail: this.state }));
  }

  async load() {
    this.social?.disconnect();
    this.social = null;
    this.requestController?.abort();
    this.requestController = new AbortController();
    const { signal } = this.requestController;
    this.state = emptyState();
    if (this.dataset.authenticated !== 'true') {
      this.state.customerStatus = 'guest';
      this.publish();
      return;
    }
    this.publish();
    const preview = window.Shopify?.designMode ? this.dataset.preview : 'auto';
    if (preview && preview !== 'auto') {
      if (preview === 'loading') return;
      const status = preview === 'error' ? 'error' : 'ready';
      this.state = { customerStatus: status, customer: status === 'ready' ? { state: 'member' } : null, wallet: { status, data: { balance: Number(this.dataset.previewPoints) || 0 } }, vip: { status }, rules: { status, data: [] }, products: { status, data: [] } };
      this.publish();
      return;
    }
    try {
      const sdk = await waitForSmileCustomer(signal);
      if (signal.aborted) return;
      this.sdk = sdk;
      this.state.customer = sdk.customer.current();
      this.state.customerStatus = 'ready';
      this.publish();
      if (this.state.customer.state === 'member') {
        this.startSocial(sdk, signal);
      }
      // A failed VIP/rules response must not hide a valid wallet.
      await Promise.allSettled([
        ['wallet', 'customerPointsWallet'], ['vip', 'customerVipStatus'], ['rules', 'earningRules'], ['products', 'customerPointsProducts']
      ].map(async ([key, resource]) => {
        try {
          const data = await boundedRequest(Promise.resolve().then(() => sdk[resource].get()), signal);
          if (key === 'wallet' && !Number.isFinite(data?.balance)) throw new Error('Invalid wallet');
          if ((key === 'rules' || key === 'products') && !Array.isArray(data)) throw new Error(`Invalid ${key}`);
          if (key === 'vip' && (!data || !('vipTier' in data))) throw new Error('Invalid VIP state');
          if (!signal.aborted) this.state[key] = { status: 'ready', data };
        } catch (error) {
          if (!signal.aborted) this.state[key] = { status: 'error' };
        }
        if (!signal.aborted && this.isConnected) this.publish();
      }));
    } catch (error) {
      if (signal.aborted || !this.isConnected) return;
      this.state.customerStatus = 'error';
      for (const key of ['wallet', 'vip', 'rules', 'products']) this.state[key] = { status: 'error' };
      this.publish();
    }
  }

  async startSocial(sdk, signal) {
    try {
      const { LoyaltySocial } = await import(this.dataset.socialModule);
      await boundedRequest(Promise.resolve().then(() => window.SmileUI.ready()), signal);
      if (signal.aborted || !this.isConnected) return;
      this.social = new LoyaltySocial(this, async () => {
        const data = await boundedRequest(sdk.customerPointsWallet.get(), signal);
        if (signal.aborted || !Number.isFinite(data?.balance)) return;
        this.state.wallet = { status: 'ready', data };
        this.publish();
      });
      await this.social.refresh();
    } catch {
      if (!signal.aborted) {
        this.querySelector('[data-social-message]').textContent = this.dataset.unavailable;
        this.querySelector('[data-earn-feedback] [data-program-retry]').hidden = false;
      }
    }
  }

  render() {
    const state = this.state;
    this.querySelectorAll('[data-earn-action="account"]').forEach(card => {
      const completed = state.customer?.state === 'member';
      card.dataset.completed = String(completed);
      card.setAttribute('aria-disabled', String(completed));
      card.tabIndex = completed ? -1 : 0;
      if (completed) card.removeAttribute('href');
      card.querySelector('.loyalty-earn__check').hidden = !completed;
      card.querySelector('[data-task-completed]').hidden = !completed;
    });
    const wallet = this.querySelector('[data-program-wallet]');
    if (wallet) {
      wallet.setAttribute('aria-busy', String(state.wallet.status === 'loading'));
      wallet.querySelector('[data-program-points]').textContent = state.wallet.status === 'ready' ? new Intl.NumberFormat(document.documentElement.lang || 'en').format(state.wallet.data.balance) : '—';
      this.querySelector('[data-wallet-message]').textContent = state.wallet.status === 'loading' ? this.dataset.loading : '';
      this.querySelector('[data-wallet-feedback] [data-program-retry]').hidden = true;
    }
    const message = this.querySelector('[data-earn-message]');
    if (message && state.customerStatus !== 'guest') {
      const error = state.customerStatus === 'error' || state.rules.status === 'error';
      const loading = state.customerStatus === 'loading' || state.rules.status === 'loading';
      message.textContent = error ? this.dataset.unavailable : loading ? this.dataset.loading : '';
      this.querySelector('[data-earn-feedback] [data-program-retry]').hidden = !error;
    }
    this.renderCoupons();
  }

  couponProduct(card) {
    const pointsPrice = Number(card.dataset.pointsPrice);
    if (!Number.isFinite(pointsPrice) || pointsPrice <= 0) return null;
    const products = Array.isArray(this.state.products?.data) ? this.state.products.data : [];
    const match = orderDiscountProduct(products);
    const id = match && fitsVariableSpend(match, pointsPrice) ? (match.pointsProduct?.id ?? match.id) : null;
    return { id, pointsPrice };
  }

  renderCoupons() {
    const ready = this.state.wallet?.status === 'ready' && this.state.products?.status === 'ready';
    const balance = ready ? this.state.wallet.data.balance : null;
    const template = this.querySelector('#loyalty-redeem-points')?.dataset.shortfall;
    this.querySelectorAll('[data-coupon]').forEach(card => {
      const shortfallEl = card.querySelector('[data-coupon-shortfall]');
      const button = card.querySelector('[data-coupon-redeem]');
      const product = this.couponProduct(card);
      const shortfall = ready && product && Number.isFinite(product.pointsPrice) ? product.pointsPrice - balance : 0;
      if (shortfallEl) {
        shortfallEl.hidden = shortfall <= 0;
        shortfallEl.replaceChildren();
        if (shortfall > 0) shortfallEl.append(fillCount(template, shortfall));
      }
      if (button) {
        button.disabled = shortfall > 0 || (ready && !product?.id);
        if (shortfall > 0) button.dataset.insufficient = 'true';
        else delete button.dataset.insufficient;
      }
    });
  }

  focusQuiet(target) {
    if (!target) return;
    target.dataset.programmaticFocus = '';
    target.focus({ preventScroll: true });
    const clear = () => {
      delete target.dataset.programmaticFocus;
      target.removeEventListener('blur', clear);
    };
    target.addEventListener('blur', clear, { once: true });
  }

  openDialog(dialog) {
    if (!dialog || dialog.open) return;
    dialog.showModal();
    this.focusQuiet(dialog.contains(document.activeElement) ? document.activeElement : null);
  }

  closeDialog(dialog) {
    if (dialog?.open) dialog.close();
  }

  async copyReferral() {
    const field = this.querySelector('[data-referral-url]');
    const url = webUrl(field?.value);
    const message = this.querySelector('[data-earn-message]');
    const status = this.querySelector('[data-referral-status]');
    if (!url) {
      const text = this.dataset.unavailable;
      if (message) message.textContent = text;
      if (status) status.textContent = text;
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      if (message) message.textContent = this.dataset.copied;
      if (status) status.textContent = this.dataset.copied;
    } catch {
      if (message) message.textContent = this.dataset.copyFailed;
      if (status) status.textContent = this.dataset.copyFailed;
      field.focus();
      field.select();
    }
  }

  async handleClick(event) {
    if (event.target.closest('[data-earn-action][data-completed="true"]')) {
      event.preventDefault();
      return;
    }
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const closer = event.target.closest('[data-dialog-close]');
    if (closer) {
      event.preventDefault();
      this.closeDialog(closer.closest('dialog'));
      return;
    }
    if (event.target.matches('.loyalty-dialog')) {
      const rect = event.target.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) {
        this.closeDialog(event.target);
      }
      return;
    }
    if (event.target.closest('[data-referral-copy]')) {
      event.preventDefault();
      await this.copyReferral();
      return;
    }
    const platform = event.target.closest('[data-social-platform]');
    if (platform) {
      event.preventDefault();
      if (platform.dataset.completed === 'true' || platform.disabled) return;
      this.closeDialog(platform.closest('dialog'));
      const message = this.querySelector('[data-earn-message]');
      if (this.social) this.social.activate(platform.dataset.socialPlatform);
      else if (message) message.textContent = this.dataset.loading;
      return;
    }
    const link = event.target.closest('[data-loyalty-panel]');
    if (link && this.contains(link)) {
      if (!window.SmileUI?.ready || !window.SmileUI?.openPanel) return;
      event.preventDefault();
      try {
        await openSmilePanel(link, this.controller.signal);
      } catch (error) { if (error.name !== 'AbortError') window.location.assign(link.href); }
      return;
    }
    if (event.target.closest('[data-program-retry]')) { event.preventDefault(); this.load(); return; }
    if (event.target.closest('[data-coupon-rules]')) {
      event.preventDefault();
      this.openDialog(this.querySelector('[data-rules-dialog]'));
      return;
    }
    if (event.target.closest('[data-week-terms]')) {
      event.preventDefault();
      this.openDialog(this.querySelector('[data-week-terms-dialog]'));
      return;
    }
    if (event.target.closest('[data-confirm-cancel]')) {
      event.preventDefault();
      this.pendingProduct = null;
      this.closeDialog(this.querySelector('[data-confirm-dialog]'));
      return;
    }
    if (event.target.closest('[data-confirm-accept]')) {
      event.preventDefault();
      this.closeDialog(this.querySelector('[data-confirm-dialog]'));
      const product = this.pendingProduct;
      this.pendingProduct = null;
      if (product) await this.purchase(product);
      return;
    }
    if (event.target.closest('[data-redeem-copy]')) {
      event.preventDefault();
      await this.copyRedeemCode();
      return;
    }
    const redeem = event.target.closest('[data-coupon-redeem]');
    if (redeem) {
      event.preventDefault();
      if (redeem.disabled) return;
      await this.startRedeem(redeem.closest('[data-coupon]'));
      return;
    }
    if (event.target.closest('button[data-pending]')) {
      event.preventDefault();
      this.querySelector('[data-redeem-message]').textContent = this.dataset.pending;
      return;
    }
    const step = event.target.closest('.loyalty-how__step');
    if (step?.hash) {
      const navLink = this.querySelector(`loyalty-navigation a[href="${step.hash}"]`);
      if (navLink) {
        event.preventDefault();
        navLink.click();
        return;
      }
    }
    const card = event.target.closest('[data-earn-action]');
    if (!card || this.dataset.authenticated !== 'true') return;
    const action = card.dataset.earnAction;
    if (action === 'purchase' || action === 'events') return;
    event.preventDefault();
    const message = this.querySelector('[data-earn-message]');
    if (this.state.customer?.state !== 'member') {
      message.textContent = this.state.customerStatus === 'loading' ? this.dataset.loading : this.dataset.unavailable;
      return;
    }
    if (action === 'account') { message.textContent = this.dataset.memberDone; return; }
    if (action === 'referral') {
      let customer = this.state.customer;
      try { customer = window.Smile?.customer?.current?.() || customer; } catch { /* Keep the last loaded customer. */ }
      const url = webUrl(customer?.referralUrl);
      const field = this.querySelector('[data-referral-url]');
      const status = this.querySelector('[data-referral-status]');
      if (status) status.textContent = '';
      if (!url || !field) { message.textContent = this.dataset.unavailable; return; }
      field.value = url;
      this.openDialog(this.querySelector('[data-referral-dialog]'));
      return;
    }
    if (action === 'follow') {
      this.openDialog(this.querySelector('[data-social-dialog]'));
      return;
    }
  }

  redeemFailed() {
    return this.querySelector('#loyalty-redeem-points')?.dataset.redeemFailed || this.dataset.unavailable;
  }

  async startRedeem(card) {
    const message = this.querySelector('[data-redeem-message]');
    if (this.dataset.authenticated !== 'true') return;
    if (this.state.customerStatus === 'loading' || this.state.products?.status === 'loading' || this.state.wallet.status === 'loading') {
      if (message) message.textContent = this.dataset.loading;
      return;
    }
    if (this.state.customer?.state !== 'member' || this.state.products?.status !== 'ready' || this.state.wallet.status !== 'ready') {
      if (message) message.textContent = this.dataset.unavailable;
      return;
    }
    const product = this.couponProduct(card);
    if (!product?.id || product.pointsPrice > this.state.wallet.data.balance) {
      if (message) message.textContent = this.redeemFailed();
      return;
    }
    if (message) message.textContent = '';
    try {
      const fulfillments = await boundedRequest(Promise.resolve().then(() => this.sdk.rewardFulfillments.get()), this.controller.signal);
      if (this.controller.signal.aborted) return;
      if (unusedFulfillment(fulfillments)) {
        this.pendingProduct = product;
        this.openDialog(this.querySelector('[data-confirm-dialog]'));
        return;
      }
      await this.purchase(product);
    } catch (error) {
      if (error.name === 'AbortError') return;
      if (message) message.textContent = this.redeemFailed();
    }
  }

  async purchase(product) {
    if (this.redeeming) return;
    const message = this.querySelector('[data-redeem-message]');
    if (!this.sdk?.pointsProducts?.purchase || !product?.id || !Number.isFinite(product.pointsPrice) || product.pointsPrice <= 0) {
      if (message) message.textContent = this.redeemFailed();
      return;
    }
    this.redeeming = true;
    this.querySelectorAll('[data-coupon-redeem]').forEach(button => button.setAttribute('aria-busy', 'true'));
    try {
      const result = await boundedRequest(Promise.resolve().then(() => this.sdk.pointsProducts.purchase(product.id, { pointsToSpend: product.pointsPrice })), this.controller.signal);
      if (this.controller.signal.aborted) return;
      const code = result?.rewardFulfillment?.code || result?.reward_fulfillment?.code;
      if (!code) throw new Error('Missing reward code');
      this.showSuccess(code);
      const wallet = await boundedRequest(Promise.resolve().then(() => this.sdk.customerPointsWallet.get()), this.controller.signal);
      if (this.controller.signal.aborted) return;
      if (Number.isFinite(wallet?.balance)) {
        this.state.wallet = { status: 'ready', data: wallet };
        this.publish();
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      if (message) message.textContent = this.redeemFailed();
    } finally {
      this.redeeming = false;
      this.querySelectorAll('[data-coupon-redeem]').forEach(button => button.removeAttribute('aria-busy'));
    }
  }

  showSuccess(code) {
    const codeEl = this.querySelector('[data-redeem-code]');
    const status = this.querySelector('[data-success-status]');
    if (codeEl) codeEl.textContent = code;
    if (status) status.textContent = '';
    this.openDialog(this.querySelector('[data-success-dialog]'));
  }

  async copyRedeemCode() {
    const code = this.querySelector('[data-redeem-code]')?.textContent?.trim();
    const status = this.querySelector('[data-success-status]');
    if (!code) {
      if (status) status.textContent = this.dataset.copyFailed;
      return;
    }
    try {
      await navigator.clipboard.writeText(code);
      if (status) status.textContent = this.dataset.copied;
    } catch {
      if (status) status.textContent = this.dataset.copyFailed;
    }
  }
}

// Count totals across separate property/selling-plan lines of the same variant.
export function cartVariantQuantities(cart) {
  const totals = new Map();
  for (const item of cart.items || []) {
    const id = String(item.variant_id);
    totals.set(id, (totals.get(id) || 0) + item.quantity);
  }
  return totals;
}

export function bundleWasAdded(before, after, items) {
  const previous = cartVariantQuantities(before);
  const current = cartVariantQuantities(after);
  return items.every(item => (current.get(String(item.id)) || 0) >= (previous.get(String(item.id)) || 0) + item.quantity);
}

let bundleSubmitting = false;
class LoyaltyBundle extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.button = this.querySelector('[data-bundle-add]');
    this.shopLabel = this.dataset.shopLabel || this.button?.textContent || '';
    this.soldOutLabel = this.dataset.soldOutLabel || 'Sold out';
    this.button?.addEventListener('click', () => this.add(), { signal: this.controller.signal });
    this.syncAvailability();
  }
  disconnectedCallback() { this.controller?.abort(); }

  parseIds() {
    return (this.dataset.variantIds || '').split(',').map(id => id.trim()).filter(Boolean);
  }

  markSoldOut() {
    this.dataset.available = 'false';
    if (!this.button) return;
    this.button.disabled = true;
    this.button.textContent = this.soldOutLabel;
    this.button.removeAttribute('aria-busy');
  }

  restoreShopButton() {
    this.dataset.available = 'true';
    if (!this.button) return;
    this.button.disabled = false;
    this.button.textContent = this.shopLabel;
  }

  async mastersAvailable(ids) {
    const { checkVariantAvailability } = await import('gift-config');
    for (const id of ids) {
      if (!this.isConnected) return false;
      if (!(await checkVariantAvailability(id))) return false;
    }
    return true;
  }

  async syncAvailability() {
    if (this.dataset.authenticated !== 'true') return;
    const ids = this.parseIds();
    if (!ids.length || ids.some(id => !/^\d+$/.test(id)) || new Set(ids).size !== ids.length) {
      this.markSoldOut();
      return;
    }
    if (!(await this.mastersAvailable(ids))) {
      if (this.isConnected) this.markSoldOut();
      return;
    }
    if (this.isConnected) this.restoreShopButton();
  }

  async add() {
    if (bundleSubmitting || this.dataset.authenticated !== 'true') return;
    const ids = this.parseIds();
    if (!ids.length || ids.some(id => !/^\d+$/.test(id)) || new Set(ids).size !== ids.length) {
      this.markSoldOut();
      return;
    }
    bundleSubmitting = true;
    const button = this.button;
    const message = this.querySelector('[data-bundle-message]');
    const program = this.closest('loyalty-program');
    const cartUrl = `${window.Shopify?.routes?.root || '/'}cart`;
    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }
    if (message) message.textContent = '';
    this.querySelector('[data-bundle-check-cart]').hidden = true;
    let before;
    let submitted = false;
    let uncertain = false;
    const baseItems = ids.map(id => ({ id, quantity: 1 }));
    // Same gift selection and fetch interceptor as existing campaign add-to-cart.
    const readCart = async () => {
      const { createCartPromise } = await import('cart-section-utils');
      const cart = await boundedRequest(createCartPromise());
      if (!Array.isArray(cart?.items)) throw new Error('Cart unavailable');
      return cart;
    };
    try {
      if (!(await this.mastersAvailable(ids))) {
        this.markSoldOut();
        return;
      }
      const { appendBundleGiftItems } = await import('gift-config');
      before = await readCart();
      const items = await boundedRequest(appendBundleGiftItems(baseItems));
      submitted = true;
      const response = await boundedRequest(fetch(`${cartUrl}/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify({ items }) }));
      if (!response.ok) {
        const data = await boundedRequest(response.json());
        // Shopify may add the available quantity even when an inventory limit produces 422.
        throw new Error(data.description || program.dataset.cartError);
      }
      await boundedRequest(response.json());
      window.location.assign(cartUrl);
    } catch (error) {
      if (submitted) {
        try {
          const after = await readCart();
          if (bundleWasAdded(before, after, baseItems)) { window.location.assign(cartUrl); return; }
        } catch { /* An uncertain add must never be retried automatically. */ }
        uncertain = true;
      }
      if (message) message.textContent = program.dataset[uncertain ? 'cartUnknown' : 'cartError'];
      this.querySelector('[data-bundle-check-cart]').hidden = !uncertain;
    } finally {
      bundleSubmitting = false;
      button?.removeAttribute('aria-busy');
      if (this.dataset.available === 'true') {
        if (button) button.disabled = uncertain;
      } else {
        this.markSoldOut();
      }
    }
  }
}

class LoyaltyMember extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.messages = JSON.parse(this.querySelector('[data-member-messages]').textContent);
    this.program = this.closest('loyalty-program');
    this.querySelector('[data-member-retry]').addEventListener('click', () => this.program ? this.program.load() : this.load(), { signal: this.controller.signal });
    this.program?.addEventListener('loyalty:data', () => this.load(), { signal: this.controller.signal });
    this.load();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.requestController?.abort();
  }

  async load() {
    this.requestController?.abort();
    this.requestController = new AbortController();
    const { signal } = this.requestController;
    this.render({ status: 'loading' });
    // Both server-rendered preview attributes AND Shopify's editor flag are required.
    const preview = window.Shopify?.designMode ? this.dataset.preview : 'auto';
    if (preview && preview !== 'auto') {
      if (preview === 'loading' || preview === 'error') return this.render({ status: preview });
      const index = TIER_KEYS.indexOf(preview);
      const tierName = index < 0 ? null : this.messages.tiers[preview];
      const nextTier = index >= 0 && index < 3 ? this.messages.tiers[TIER_KEYS[index + 1]] : null;
      const points = Number(this.dataset.previewPoints);
      return this.render({ status: 'ready', points: Number.isFinite(points) ? points : 4250, tierKey: index < 0 ? null : preview, tierName, nextTier, progress: index < 0 ? null : index === 3 ? 100 : 72, highest: index === 3 });
    }
    if (this.program) {
      const state = this.program.state;
      if (!state || state.wallet.status === 'loading') return;
      if (state.wallet.status === 'error') return this.render({ status: 'error' });
      if (state.vip.status === 'ready') return this.render(normalizeMembership(state.wallet.data, state.vip.data));
      return this.render({ status: 'ready', points: state.wallet.data.balance, tierKnown: false, vipStatus: state.vip.status });
    }
    try {
      const sdk = await waitForSmileCustomer(signal);
      const [wallet, vipStatus] = await boundedRequest(Promise.all([
        sdk.customerPointsWallet.get(),
        sdk.customerVipStatus.get()
      ]), signal);
      if (!signal.aborted && this.isConnected) this.render(normalizeMembership(wallet, vipStatus));
    } catch (error) {
      if (!signal.aborted && this.isConnected) this.render({ status: 'error' });
    }
  }

  render(state) {
    this.dataset.state = state.status;
    this.dataset.tier = state.tierKey || '';
    this.setAttribute('aria-busy', String(state.status === 'loading'));
    const ready = state.status === 'ready';
    this.querySelector('[data-member-tier]').textContent = ready && state.tierKnown !== false ? state.tierName || this.messages.noTier : '—';
    this.querySelector('[data-member-points]').textContent = ready ? new Intl.NumberFormat(document.documentElement.lang || 'en').format(state.points) : '—';
    this.querySelectorAll('[data-badge]').forEach(badge => { badge.hidden = !ready || badge.dataset.badge !== state.tierKey; });
    const progress = this.querySelector('[data-member-progress]');
    progress.hidden = !ready || state.progress == null;
    const track = progress.querySelector('[role="progressbar"]');
    if (!progress.hidden) {
      track.style.setProperty('--loyalty-progress', `${state.progress}%`);
      track.setAttribute('aria-valuenow', state.progress);
      const label = state.highest ? this.messages.highest : this.messages.progress.replace('[percent]', state.progress).replace('[tier]', state.nextTier);
      track.setAttribute('aria-valuetext', label);
      this.querySelector('[data-member-progress-text]').textContent = label;
    } else {
      track.removeAttribute('aria-valuenow');
      track.removeAttribute('aria-valuetext');
    }
    this.querySelector('[data-member-status]').textContent = ready ? state.tierKnown === false ? this.messages[state.vipStatus] : '' : this.messages[state.status];
    this.querySelector('[data-member-retry]').hidden = state.status !== 'error' && state.vipStatus !== 'error';
  }
}

class LoyaltyNavigation extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.program = this.closest('loyalty-program');
    this.items = Array.from(this.querySelectorAll('a')).flatMap(link => {
      const target = document.getElementById(link.hash.slice(1));
      return target ? [{ link, target }] : [];
    });
    this.syncHeight();
    this.syncState();
    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver(() => {
      this.syncHeight();
      this.syncState();
    });
    this.resizeObserver.observe(this);
    this.querySelectorAll('a').forEach(link => {
      link.addEventListener('click', event => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const target = document.getElementById(link.hash.slice(1));
        if (!target) return;
        event.preventDefault();
        this.activate(link);
        const padding = parseFloat(getComputedStyle(document.documentElement).scrollPaddingBlockStart) || 0;
        const margin = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
        const top = Math.max(0, target.getBoundingClientRect().top + window.scrollY - padding - margin);
        const behavior = this.scrollBehavior();
        const hash = `${location.pathname}${location.search}${link.hash}`;
        setTimeout(() => {
          window.scrollTo({ top, behavior });
          history.replaceState(null, '', hash);
        }, 0);
      }, { signal });
    });
    const current = this.items.find(item => item.link.hash === window.location.hash);
    if (current) this.activate(current.link);
    window.addEventListener('hashchange', () => {
      const item = this.items.find(({ link }) => link.hash === window.location.hash);
      if (item) this.activate(item.link);
    }, { signal });
    window.addEventListener('scroll', () => this.scheduleSync(), { passive: true, signal });
    window.addEventListener('resize', () => this.scheduleSync(), { passive: true, signal });
  }

  scrollBehavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  scheduleSync() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      this.syncState();
    });
  }

  syncHeight() {
    this.program?.style.setProperty('--loyalty-nav-height', `${Math.round(this.getBoundingClientRect().height)}px`);
  }

  syncState() {
    this.classList.toggle('is-stuck', this.getBoundingClientRect().top <= 0.5);
    if (!this.items.length) return;
    const padding = parseFloat(getComputedStyle(document.documentElement).scrollPaddingBlockStart) || 0;
    const line = Math.max(this.getBoundingClientRect().bottom, padding);
    let current = this.items[0];
    for (const item of this.items) {
      if (item.target.getBoundingClientRect().top <= line + 1) current = item;
    }
    this.activate(current.link);
  }

  activate(current) {
    if (!current || this.active === current) return;
    this.active = current;
    this.querySelectorAll('a').forEach(link => {
      if (link === current) link.setAttribute('aria-current', 'location');
      else link.removeAttribute('aria-current');
    });
    const nav = this.querySelector('nav');
    if (!nav) return;
    const left = current.offsetLeft - nav.offsetLeft;
    if (left < nav.scrollLeft || left + current.offsetWidth > nav.scrollLeft + nav.clientWidth) {
      nav.scrollTo({ left: left - (nav.clientWidth - current.offsetWidth) / 2, behavior: this.scrollBehavior() });
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
    if (this.frame) cancelAnimationFrame(this.frame);
    this.resizeObserver?.disconnect();
    this.program?.style.removeProperty('--loyalty-nav-height');
  }
}

if (!customElements.get('loyalty-program')) customElements.define('loyalty-program', LoyaltyProgram);
if (!customElements.get('loyalty-bundle')) customElements.define('loyalty-bundle', LoyaltyBundle);
if (!customElements.get('loyalty-member')) customElements.define('loyalty-member', LoyaltyMember);
if (!customElements.get('loyalty-navigation')) customElements.define('loyalty-navigation', LoyaltyNavigation);

class LoyaltyTiers extends HTMLElement {
  connectedCallback() {
    this.disconnectedCallback();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.program = this.closest('loyalty-program');
    this.scroller = this.querySelector('.loyalty-tiers__scroll');
    this.media = matchMedia('(max-width: 720px)');
    this.centered = false;
    this.userScrolled = false;
    this.inView = false;
    this.program?.addEventListener('loyalty:data', () => this.render(), { signal });
    this.media.addEventListener('change', () => this.centerCurrentTier(), { signal });
    for (const type of ['pointerdown', 'touchstart', 'wheel', 'keydown']) {
      this.scroller.addEventListener(type, () => { this.userScrolled = true; }, { signal, passive: true });
    }
    this.observer = new IntersectionObserver(entries => {
      this.inView = entries[0].isIntersecting;
      this.centerCurrentTier();
    });
    this.observer.observe(this);
    this.resizeObserver = new ResizeObserver(() => {
      const table = this.querySelector('.loyalty-tiers__table');
      this.style.setProperty('--tier-table-height', `${table.getBoundingClientRect().height}px`);
      this.centerCurrentTier();
    });
    this.resizeObserver.observe(this.scroller);
    this.render();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    this.resizeObserver?.disconnect();
  }

  render() {
    const state = this.program?.state;
    if (!state) return;
    const guest = this.program.dataset.authenticated !== 'true';
    let wallet = state.wallet;
    let vip = state.vip;
    const preview = window.Shopify?.designMode ? this.program.dataset.preview : 'auto';
    if (!guest && preview && preview !== 'auto' && preview !== 'loading' && preview !== 'error') {
      const name = TIER_KEYS.includes(preview) ? preview : null;
      vip = { status: 'ready', data: { vipTier: name ? { name } : null } };
      wallet = { status: 'ready', data: { balance: Number(this.program.dataset.previewPoints) } };
    }
    const name = guest || vip.status !== 'ready' ? null : vip.data?.vipTier?.name;
    const emblems = Array.from(this.querySelectorAll('[data-tier-emblem]'));
    const current = name ? emblems.find(item => [item.dataset.tierEmblem, item.dataset.tierName].some(label => label.trim().toLowerCase() === name.trim().toLowerCase())) : null;
    this.dataset.tier = current?.dataset.tierEmblem || '';
    emblems.forEach(emblem => emblem.classList.toggle('is-current', emblem === current));
    this.querySelectorAll('[data-tier-cell]').forEach(cell => {
      const active = Boolean(current && cell.dataset.tierCell === this.dataset.tier);
      cell.classList.toggle('is-current', active);
      if (cell.scope === 'col') {
        if (active) cell.setAttribute('aria-current', 'true');
        else cell.removeAttribute('aria-current');
      }
    });
    const points = wallet.status === 'ready' && Number.isFinite(wallet.data?.balance) ? new Intl.NumberFormat(document.documentElement.lang || 'en').format(wallet.data.balance) : '—';
    const tierName = vip.status === 'ready' ? current?.dataset.tierName || name || this.dataset.noTier : '—';
    this.querySelectorAll('[data-tier-summary]').forEach(summary => {
      summary.textContent = `${points} ${this.dataset.pointsLabel} · ${tierName}`;
      const emblem = summary.closest('[data-tier-emblem]');
      summary.hidden = guest || (emblem ? emblem !== current : false);
    });
    const status = this.querySelector('[data-tier-status]');
    if (status) {
      const failed = vip.status === 'error' || wallet.status === 'error';
      const loading = vip.status === 'loading' || wallet.status === 'loading';
      status.textContent = guest || failed || loading ? '' : !name ? this.dataset.noTier : '';
      this.querySelector('[data-program-retry]').hidden = true;
      this.setAttribute('aria-busy', String(!guest && loading));
    }
    this.centerCurrentTier();
  }

  centerCurrentTier() {
    if (!this.media?.matches || !this.inView || this.centered || this.userScrolled || !this.dataset.tier) return;
    const current = this.querySelector(`thead [data-tier-cell="${this.dataset.tier}"]`);
    const fixed = this.querySelector('thead tr > :first-child');
    if (!current || !this.scroller.clientWidth) return;
    const viewport = this.scroller.getBoundingClientRect();
    const column = current.getBoundingClientRect();
    const frozenWidth = fixed.getBoundingClientRect().width;
    const center = viewport.left + frozenWidth + (this.scroller.clientWidth - frozenWidth) / 2;
    const left = this.scroller.scrollLeft + column.left + column.width / 2 - center;
    this.scroller.scrollTo({ left: Math.max(0, Math.min(left, this.scroller.scrollWidth - this.scroller.clientWidth)), behavior: 'instant' });
    this.centered = true;
  }
}

// Reuse Impact's disclosure semantics/click handling, with a cancelable local
// height transition so quick toggles and category changes cannot race.
class LoyaltyFaqDisclosure extends AccordionDisclosure {
  setImmediately(value) {
    this.motion?.cancel();
    this.summaryMotion?.cancel();
    this.motion = null;
    this.summaryMotion = null;
    this._open = value;
    this.toggleAttribute('open', value);
    this.setAttribute('aria-expanded', String(value));
    this.style.height = '';
    this.style.overflow = '';
  }

  _transition(value) {
    const start = this.getBoundingClientRect().height;
    const startGap = getComputedStyle(this).marginBottom;
    const summary = this.firstElementChild;
    const startPadding = getComputedStyle(summary).padding;
    this.motion?.cancel();
    this.summaryMotion?.cancel();
    this.motion = null;
    this.summaryMotion = null;
    this.style.height = '';
    this.style.overflow = '';
    this.toggleAttribute('open', value);
    const end = this.getBoundingClientRect().height;
    const endGap = getComputedStyle(this).marginBottom;
    const endPadding = getComputedStyle(summary).padding;
    this.dispatchEvent(new CustomEvent('loyalty:faq-toggle', { bubbles: true, detail: { open: value } }));
    if (matchMedia('(prefers-reduced-motion: reduce)').matches || start === end) return;
    // Keep the answer painted, and animate its entire occupied space (including
    // the gap and summary padding) so closing does not end with a layout jump.
    this.setAttribute('open', '');
    this.style.overflow = 'hidden';
    this.setAttribute('aria-expanded', String(value));
    const timing = { duration: 360, easing: 'cubic-bezier(.22, 1, .36, 1)', fill: 'both' };
    const summaryMotion = summary.animate([{ padding: startPadding }, { padding: endPadding }], timing);
    const motion = this.animate([{ height: `${start}px`, marginBottom: startGap }, { height: `${end}px`, marginBottom: endGap }], timing);
    this.summaryMotion = summaryMotion;
    this.motion = motion;
    motion.finished.then(() => {
      if (this.motion !== motion) return;
      this.motion = null;
      this.toggleAttribute('open', this.open);
      this.setAttribute('aria-expanded', String(this.open));
      motion.cancel();
      summaryMotion.cancel();
      this.summaryMotion = null;
      this.style.overflow = '';
    }).catch(() => {});
  }

  disconnectedCallback() {
    this.motion?.cancel();
    this.summaryMotion?.cancel();
  }
}

class LoyaltyFaq extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.media = matchMedia('(max-width: 720px)');
    this.category = this.querySelector('[data-faq-category]')?.dataset.faqCategory;
    this.questions = Array.from(this.querySelectorAll('.loyalty-faq__question'));
    this.addEventListener('click', event => {
      const button = event.target.closest('[data-faq-category]');
      if (button) this.selectCategory(button.dataset.faqCategory);
    }, { signal });
    this.addEventListener('loyalty:faq-toggle', event => {
      if (event.detail.open) this.questions.forEach(item => { if (item !== event.target && item.open) item.open = false; });
    }, { signal });
    this.media.addEventListener('change', () => this.selectCategory(this.category), { signal });
    this.addEventListener('shopify:block:select', event => {
      const group = event.target.closest('[data-faq-group]');
      if (group && !this.media.matches) this.selectCategory(group.dataset.faqGroup);
      const question = event.target.closest('.loyalty-faq__question');
      if (question) question.open = true;
    }, { signal });
    this.selectCategory(this.category);
  }

  selectCategory(category) {
    this.category = category;
    this.querySelectorAll('[data-faq-category]').forEach(button => {
      if (button.dataset.faqCategory === category) button.setAttribute('aria-current', 'true');
      else button.removeAttribute('aria-current');
    });
    this.querySelectorAll('[data-faq-group]').forEach(group => group.classList.toggle('is-current', group.dataset.faqGroup === category));
    const first = this.media.matches ? this.questions[0] : this.questions.find(item => item.closest('[data-faq-group]').dataset.faqGroup === category);
    this.questions.forEach(item => item.setImmediately(item === first));
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.questions?.forEach(item => item.motion?.cancel());
  }
}

if (!customElements.get('loyalty-tiers')) customElements.define('loyalty-tiers', LoyaltyTiers);
if (!customElements.get('loyalty-faq-disclosure')) customElements.define('loyalty-faq-disclosure', LoyaltyFaqDisclosure, { extends: 'details' });
if (!customElements.get('loyalty-faq')) customElements.define('loyalty-faq', LoyaltyFaq);
